import { Button, FormField, Input, useToast } from "@design";
import { useMutation } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { type AiModelSuggestResponse, adminSuggestModelMutation } from "@/api";
import { PageSection } from "@/components/section/SectionBlocks";
import { Hint } from "./Hint";

/** "Quick setup": AI suggests a configuration for a model name, which pre-fills the form. */
export function SuggestBar({ onSuggested }: Readonly<{ onSuggested: (s: AiModelSuggestResponse) => void }>) {
  const { t } = useTranslation("adminModels");
  const toast = useToast();
  const id = useId();
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const suggest = useMutation(adminSuggestModelMutation());

  const run = () => {
    const modelName = name.trim();
    if (!modelName) {
      toast.warning(t("suggest.nameRequired"));
      return;
    }
    setNotes("");
    suggest.mutate(
      { body: { modelName } },
      {
        onSuccess: ({ data }) => {
          onSuggested(data);
          setNotes(data.notes ?? "");
          toast.success(t("suggest.done"));
        },
        onError: () => toast.error(t("suggest.failed")),
      },
    );
  };

  return (
    <PageSection title={t("suggest.title")} description={t("suggest.description")}>
      <div className="flex flex-col gap-2 md:flex-row md:items-end">
        <div className="md:flex-1">
          <FormField label={t("suggest.label")} htmlFor={id}>
            <Input
              id={id}
              value={name}
              placeholder={t("suggest.placeholder")}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  run();
                }
              }}
            />
          </FormField>
        </div>
        <Button variant="secondary" leadingIcon={<Sparkles size={16} />} loading={suggest.isPending} onClick={run}>
          {t("suggest.action")}
        </Button>
      </div>
      {notes && <Hint>{notes}</Hint>}
    </PageSection>
  );
}
